import React from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { Link } from 'react-router-dom';
import { FREE_PLAN_BOOKMARK_LIMIT } from '../services/bookmarks-check-service';

/**
 * BookmarkLimitBanner Component
 * -------------------------
 * This component displays a banner when a free user is approaching or has reached
 * the bookmark limit, encouraging them to upgrade to premium.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.limitReached - Whether the user has reached the bookmark limit
 * @param {boolean} props.approaching - Whether the user is approaching the bookmark limit
 * @param {number} props.totalBookmarks - Total number of bookmarks the user has
 * @param {number} props.displayedBookmarks - Number of bookmarks being displayed
 * @returns {JSX.Element|null} The banner component or null if not needed
 */
export default function BookmarkLimitBanner({ 
  limitReached, 
  approaching, 
  totalBookmarks, 
  displayedBookmarks 
}) {
  // Don't show banner if neither condition is met
  if (!limitReached && !approaching) {
    return null;
  }

  // Determine banner style and content based on status
  const severity = limitReached ? 'error' : 'warning';
  const title = limitReached ? 
    '🔒 Bookmark Limit Reached' : 
    '⚠️ Approaching Bookmark Limit';
  
  const message = limitReached ?
    `You've reached the limit of ${FREE_PLAN_BOOKMARK_LIMIT} bookmarks on the free plan. Upgrade to Premium for unlimited bookmarks.` :
    `You have ${totalBookmarks}/${FREE_PLAN_BOOKMARK_LIMIT} bookmarks. Upgrade to Premium for unlimited bookmarks.`;

  // If we're limiting the display, show a message about hidden bookmarks
  const hiddenCount = totalBookmarks - displayedBookmarks;
  const hiddenMessage = hiddenCount > 0 ? 
    `${hiddenCount} bookmark${hiddenCount > 1 ? 's are' : ' is'} hidden. Upgrade to access all your bookmarks.` : 
    null;

  return (
    <Box sx={{ mb: 3 }}>
      <Alert 
        severity={severity}
        action={
          <Button 
            color="inherit" 
            size="small" 
            component={Link} 
            to="/subscriptions"
            variant="outlined"
          >
            Upgrade
          </Button>
        }
        sx={{ mb: hiddenMessage ? 1 : 0 }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
        <Typography variant="body2">
          {message}
        </Typography>
      </Alert>
      
      {hiddenMessage && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {hiddenMessage}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Creates a warning banner for hidden bookmarks
 * @param {number} totalBookmarks - Total number of bookmarks the user has
 * @param {number} displayedBookmarks - Number of bookmarks being displayed
 * @param {number} limit - The bookmark limit for free users
 * @returns {JSX.Element|null} The warning banner element or null if not needed
 */
export function HiddenBookmarksWarning({ totalBookmarks, displayedBookmarks, limit = FREE_PLAN_BOOKMARK_LIMIT }) {
  // Only create warning if we're actually hiding bookmarks
  if (totalBookmarks <= displayedBookmarks) {
    return null;
  }
  
  const hiddenCount = totalBookmarks - displayedBookmarks;
  
  return (
    <Alert 
      severity="warning" 
      sx={{ mt: 2 }}
      action={
        <Button 
          color="inherit" 
          size="small" 
          component={Link} 
          to="/subscriptions"
          variant="outlined"
        >
          Upgrade
        </Button>
      }
    >
      <Typography variant="subtitle2">
        ⚠️ {hiddenCount} bookmark{hiddenCount > 1 ? 's are' : ' is'} hidden
      </Typography>
      <Typography variant="body2">
        Free plan is limited to {limit} bookmarks. Upgrade to Premium to access all your bookmarks.
      </Typography>
    </Alert>
  );
}
